const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

export interface AdSenseConfig {
  enabled:      boolean;
  publisher_id: string;
  placements: {
    banner_enabled:      boolean;
    banner_slot_id:      string;
    in_content_enabled:  boolean;
    in_content_slot_id:  string;
    sidebar_enabled:     boolean;
    sidebar_slot_id:     string;
  };
}

export const DEFAULT_ADSENSE_CONFIG: AdSenseConfig = {
  enabled:      false,
  publisher_id: '',
  placements: {
    banner_enabled:      true,
    banner_slot_id:      '',
    in_content_enabled:  true,
    in_content_slot_id:  '',
    sidebar_enabled:     false,
    sidebar_slot_id:     '',
  },
};

// In-memory cache — fetched once per page load
let _cache: AdSenseConfig | null = null;
let _fetchPromise: Promise<AdSenseConfig> | null = null;

export async function fetchAdSenseConfig(): Promise<AdSenseConfig> {
  if (_cache) return _cache;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = fetch(`${API_BASE}/api/cms/settings/adsense`)
    .then(r => {
      if (!r.ok) return DEFAULT_ADSENSE_CONFIG;
      return r.json() as Promise<{ value: AdSenseConfig }>;
    })
    .then(body => {
      const cfg = (body && typeof body === 'object' && 'value' in body)
        ? { ...DEFAULT_ADSENSE_CONFIG, ...(body as { value: AdSenseConfig }).value }
        : DEFAULT_ADSENSE_CONFIG;
      _cache = cfg;
      return cfg;
    })
    .catch(() => DEFAULT_ADSENSE_CONFIG)
    .finally(() => { _fetchPromise = null; });

  return _fetchPromise;
}

export function invalidateAdSenseCache(): void {
  _cache = null;
}

export async function saveAdSenseConfig(
  config: AdSenseConfig,
  token: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/settings/adsense`, {
    method:  'PUT',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ value: config }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Save failed (${res.status})`);
  }
  invalidateAdSenseCache();
}
